import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ComponentProps, ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Boost } from '@sorare/core/src/atoms/icons/Boost';
import { iconRotateLeft } from '@sorare/core/src/atoms/icons/system/iconRotateLeft.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { useIsBoostAffordable } from '#src/hooks/useIsBoostAffordable.tsx';

import { CountdownDialog } from './CountdownDialog/index.tsx';
import type { BoostButton_anyCard } from './__generated__/index.graphql.ts';

const StyledBoost = styled(Boost)`
  &.withOpacity {
    opacity: 0.5;
  }
`;
const StyledSpan = styled.span`
  font-weight: normal !important;
  color: var(--c-nd-700);
`;
const CooldownButton = styled(LoadingButton)`
  isolation: isolate;
  &.hasCooldown {
    overflow: hidden;
    color: var(--c-nd-600);

    > * {
      z-index: 1;
    }

    &:before {
      content: '';
      position: absolute;
      inset: 0;
      width: var(--cooldown-percent);
      background: var(--c-nd-50);
    }
  }
`;

type Props = {
  anyCard: BoostButton_anyCard;
  selected?: boolean;
  balanceUsed?: number;
  needConfirm?: boolean;
  loading?: boolean;
} & Pick<ComponentProps<typeof LoadingButton>, 'size' | 'onClick'>;

export const BoostButton = ({
  anyCard,
  selected,
  balanceUsed,
  size,
  loading,
  onClick,
  needConfirm,
}: Props) => {
  const [open, setOpen] = useState(false);
  const isBoostAffordable = useIsBoostAffordable();
  const {
    affordable,
    xpNeeded,
    hasCooldown,
    cooldownMessage,
    cooldownPercent,
  } = isBoostAffordable(anyCard, balanceUsed);

  const isMaxLevel = anyCard.xpNeededForNextGrade === null;
  const disabled = (!selected && !affordable && !hasCooldown) || isMaxLevel;

  let label: ReactNode;
  let color: ComponentProps<typeof LoadingButton>['color'] = 'quaternary';
  let startIcon: ReactNode = (
    <StyledBoost
      size={2}
      rarity={
        disabled || (anyCard.rarity === Rarity.common && needConfirm)
          ? undefined
          : anyCard.rarity
      }
      className={classNames({ withOpacity: hasCooldown })}
    />
  );
  if (selected) {
    label = (
      <Horizontal gap={0.5}>
        <FormattedMessage
          {...fantasy.undoNumber}
          values={{
            value: xpNeeded,
            span: chunks => <StyledSpan>{chunks}</StyledSpan>,
          }}
        />
      </Horizontal>
    );
    startIcon = <FontAwesomeIcon icon={iconRotateLeft} />;
  } else if (hasCooldown) {
    color = 'tertiary';
    label = cooldownMessage;
  } else if (isMaxLevel) {
    label = <FormattedMessage {...glossary.max} />;
    startIcon = null;
  } else if (needConfirm) {
    color = 'secondary';
    label = <FormattedMessage {...glossary.confirm} />;
  } else {
    label = <FormattedNumber value={xpNeeded} />;
  }

  return (
    <>
      <CooldownButton
        disableDebounce
        color={color}
        size={size}
        onClick={hasCooldown ? () => setOpen(true) : onClick}
        disabled={disabled}
        startIcon={startIcon}
        loading={loading || false}
        style={
          {
            '--cooldown-percent': `${(cooldownPercent || 0) * 100}%`,
          } as CSSProperties
        }
        className={classNames({ hasCooldown })}
      >
        {label}
      </CooldownButton>
      {!!open && !!anyCard.nextLevelUpAvailableAt && (
        <CountdownDialog
          open
          onClose={() => setOpen(false)}
          date={anyCard.nextLevelUpAvailableAt}
        />
      )}
    </>
  );
};

BoostButton.fragments = {
  anyCard: gql`
    fragment BoostButton_anyCard on AnyCardInterface {
      slug
      rarity: rarityTyped
      xpNeededForNextGrade
      lastLeveledUpAt
      nextLevelUpAvailableAt
      ...useIsBoostAffordable_anyCard
    }
    ${useIsBoostAffordable.fragments.anyCard}
  ` as TypedDocumentNode<BoostButton_anyCard>,
};
